import React, { useEffect, useRef, useState } from "react";
import QrScannerLib from "qr-scanner";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  Alert,
  CircularProgress,
  IconButton,
  TextField,
  Tabs,
  Tab,
  Stack
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import KeyboardIcon from "@mui/icons-material/Keyboard";
import FlashOnIcon from "@mui/icons-material/FlashOn";
import FlashOffIcon from "@mui/icons-material/FlashOff";
import { useSelector } from "react-redux";
import { BIBlue } from "../../../assets/helpers";

const AssetBarcodeScanner = ({ open, onClose, onScanResult }) => {
  const { selectedClientData } = useSelector((state) => state?.main || {});
  const accentColor = selectedClientData?.AccentColor || BIBlue;

  const [activeTab, setActiveTab] = useState(0);
  const videoRef = useRef(null);
  const qrScannerRef = useRef(null);
  const fileInputRef = useRef(null);

  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [hasFlash, setHasFlash] = useState(false);
  const [flashOn, setFlashOn] = useState(false);
  const [manualCode, setManualCode] = useState("");

  useEffect(() => {
    if (open) {
      setError("");
      setManualCode("");
      if (activeTab === 0) {
        const timer = setTimeout(() => {
          startCameraScanner();
        }, 300);
        return () => clearTimeout(timer);
      }
    } else {
      setError("");
      setManualCode("");
      stopCameraScanner();
    }
    return () => {
      stopCameraScanner();
    };
  }, [open, activeTab]);

  const startCameraScanner = async () => {
    try {
      setError("");
      setLoading(true);

      const hasCam = await QrScannerLib.hasCamera();
      if (!hasCam) {
        setError("No camera found on this device. You can upload an image or enter the code manually.");
        setLoading(false);
        return;
      }

      if (videoRef.current) {
        if (qrScannerRef.current) {
          qrScannerRef.current.destroy();
        }

        qrScannerRef.current = new QrScannerLib(
          videoRef.current,
          (result) => {
            const scannedText = typeof result === "object" ? result.data : result;
            if (scannedText) {
              handleCodeFound(scannedText);
            }
          },
          {
            returnDetailedScanResult: true,
            highlightScanRegion: true,
            highlightCodeOutline: true,
            maxScansPerSecond: 10
          }
        );

        await qrScannerRef.current.start();
        const flashAvailable = await qrScannerRef.current.hasFlash();
        setHasFlash(flashAvailable);
        setLoading(false);
      }
    } catch (err) {
      console.error("Camera scanner error:", err);
      setLoading(false);
      if (err?.name === "NotAllowedError" || err?.name === "PermissionDeniedError") {
        setError("Camera permission denied. Please allow camera access in your browser settings.");
      } else {
        setError("Unable to start camera stream. Try uploading an image or entering the code manually.");
      }
    }
  };

  const stopCameraScanner = () => {
    if (qrScannerRef.current) {
      try {
        qrScannerRef.current.stop();
        qrScannerRef.current.destroy();
      } catch (e) {
        console.error("Error stopping scanner:", e);
      }
      qrScannerRef.current = null;
    }
    setFlashOn(false);
  };

  const toggleFlash = async () => {
    if (qrScannerRef.current && hasFlash) {
      try {
        await qrScannerRef.current.toggleFlash();
        setFlashOn((prev) => !prev);
      } catch (e) {
        console.error("Toggle flash error:", e);
      }
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setError("");
    setLoading(true);
    try {
      const result = await QrScannerLib.scanImage(file, {
        returnDetailedScanResult: true
      });
      const text = typeof result === "object" ? result.data : result;
      if (text) {
        handleCodeFound(text);
      } else {
        setError("No barcode or QR code found in the uploaded image.");
      }
    } catch (err) {
      console.error("Image scan error:", err);
      setError("No readable QR or barcode found in this image. Please try another image or enter manually.");
    } finally {
      setLoading(false);
      e.target.value = "";
    }
  };

  const handleManualSubmit = () => {
    const code = manualCode.trim();
    if (code) {
      setManualCode("");
      handleCodeFound(code);
    }
  };

  const handleCodeFound = (code) => {
    stopCameraScanner();
    if (typeof onClose === "function") {
      onClose();
    }
    if (typeof onScanResult === "function") {
      onScanResult(code);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={() => {
        stopCameraScanner();
        if (typeof onClose === "function") {
          onClose();
        }
      }}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: "14px",
          overflow: "hidden"
        }
      }}
    >
      <DialogTitle
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          px: 3,
          py: 2,
          borderBottom: "1px solid #ECEEF2"
        }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: "#1A1D23" }}>
          Scan Asset QR / Barcode
        </Typography>
        <IconButton
          size="small"
          onClick={() => {
            stopCameraScanner();
            if (typeof onClose === "function") {
              onClose();
            }
          }}
          sx={{ color: "#6B7280" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <Box sx={{ borderBottom: 1, borderColor: "#ECEEF2", px: 2, bgcolor: "#FAFBFC" }}>
        <Tabs
          value={activeTab}
          onChange={(e, val) => {
            setActiveTab(val);
            setError("");
          }}
          sx={{
            "& .MuiTab-root": {
              textTransform: "none",
              fontWeight: 600,
              fontSize: "0.85rem",
              minHeight: 44
            },
            "& .Mui-selected": {
              color: `${accentColor} !important`
            },
            "& .MuiTabs-indicator": {
              bgcolor: accentColor
            }
          }}
        >
          <Tab icon={<CameraAltIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Camera" />
          <Tab icon={<UploadFileIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Upload Image" />
          <Tab icon={<KeyboardIcon sx={{ fontSize: 18 }} />} iconPosition="start" label="Manual Code" />
        </Tabs>
      </Box>

      <DialogContent sx={{ p: 3, bgcolor: "#FFFFFF" }}>
        {error && (
          <Alert severity="warning" sx={{ mb: 2, borderRadius: "10px", fontSize: "0.85rem" }}>
            {error}
          </Alert>
        )}

        {activeTab === 0 && (
          <Box sx={{ textAlign: "center" }}>
            <Box
              sx={{
                position: "relative",
                width: "100%",
                maxWidth: 400,
                height: 280,
                mx: "auto",
                borderRadius: "12px",
                overflow: "hidden",
                bgcolor: "#000000",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                border: "2px solid #ECEEF2"
              }}
            >
              <video
                ref={videoRef}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "cover"
                }}
                playsInline
                muted
              />

              {loading && (
                <Box
                  sx={{
                    position: "absolute",
                    inset: 0,
                    bgcolor: "rgba(0,0,0,0.6)",
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 1.5,
                    color: "#FFFFFF"
                  }}
                >
                  <CircularProgress size={36} sx={{ color: "#FFFFFF" }} />
                  <Typography variant="caption">Starting camera...</Typography>
                </Box>
              )}

              {!loading && !error && (
                <Box
                  sx={{
                    position: "absolute",
                    width: 200,
                    height: 200,
                    border: `2px solid ${accentColor}`,
                    borderRadius: "12px",
                    boxShadow: `0 0 0 9999px rgba(0, 0, 0, 0.4)`,
                    pointerEvents: "none"
                  }}
                />
              )}

              {hasFlash && (
                <IconButton
                  onClick={toggleFlash}
                  sx={{
                    position: "absolute",
                    bottom: 12,
                    right: 12,
                    bgcolor: "rgba(0,0,0,0.6)",
                    color: flashOn ? "#FACC15" : "#FFFFFF",
                    "&:hover": { bgcolor: "rgba(0,0,0,0.8)" }
                  }}
                >
                  {flashOn ? <FlashOnIcon /> : <FlashOffIcon />}
                </IconButton>
              )}
            </Box>

            <Typography variant="body2" sx={{ color: "#6B7280", mt: 2, fontSize: "0.85rem" }}>
              Point the camera at the fixed asset's QR code or Barcode tag.
            </Typography>
          </Box>
        )}

        {activeTab === 1 && (
          <Box sx={{ textAlign: "center", py: 2 }}>
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              style={{ display: "none" }}
              onChange={handleFileUpload}
            />
            <Box
              onClick={() => fileInputRef.current?.click()}
              sx={{
                p: 4,
                border: `2px dashed ${accentColor}66`,
                borderRadius: "12px",
                bgcolor: `${accentColor}06`,
                cursor: "pointer",
                transition: "all 0.2s ease",
                "&:hover": {
                  borderColor: accentColor,
                  bgcolor: `${accentColor}12`
                }
              }}
            >
              <UploadFileIcon sx={{ fontSize: 44, color: accentColor, mb: 1 }} />
              <Typography variant="subtitle2" sx={{ fontWeight: 650, color: "#1A1D23" }}>
                Click to browse or drop an asset photo here
              </Typography>
              <Typography variant="caption" sx={{ color: "#6B7280", display: "block", mt: 0.5 }}>
                Supports PNG, JLW, JPEG containing a barcode or QR
              </Typography>
            </Box>
            {loading && (
              <Box sx={{ mt: 2, display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                <CircularProgress size={18} sx={{ color: accentColor }} />
                <Typography variant="caption" sx={{ color: "#6B7280" }}>
                  Analyzing image for barcode...
                </Typography>
              </Box>
            )}
          </Box>
        )}

        {activeTab === 2 && (
          <Box
            component="form"
            onSubmit={(e) => {
              e.preventDefault();
              handleManualSubmit();
            }}
            sx={{ py: 2 }}
          >
            <Typography variant="body2" sx={{ color: "#374151", mb: 1, fontWeight: 500 }}>
              Enter Asset Code, Barcode, or Equipment Number:
            </Typography>
            <Stack direction="row" spacing={1.5}>
              <TextField
                fullWidth
                size="small"
                autoFocus
                placeholder="e.g. EE-00102, 1004829..."
                value={manualCode}
                onChange={(e) => setManualCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    handleManualSubmit();
                  }
                }}
                sx={{
                  bgcolor: "#FAFBFC",
                  "& .MuiOutlinedInput-root": {
                    borderRadius: "10px",
                    "& fieldset": { borderColor: "#ECEEF2" }
                  }
                }}
              />
              <Button
                type="submit"
                variant="contained"
                disabled={!manualCode.trim()}
                sx={{
                  borderRadius: "10px",
                  bgcolor: accentColor,
                  color: "#FFFFFF",
                  fontWeight: 650,
                  textTransform: "none",
                  px: 3,
                  boxShadow: "none",
                  "&:hover": { bgcolor: accentColor, opacity: 0.92 }
                }}
              >
                Apply
              </Button>
            </Stack>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2, borderTop: "1px solid #ECEEF2" }}>
        <Button
          onClick={() => {
            stopCameraScanner();
            onClose();
          }}
          sx={{ textTransform: "none", fontWeight: 600, color: "#6B7280" }}
        >
          Cancel
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default AssetBarcodeScanner;
